import Select from '@components/v2/form/Select/Select';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Loading from '@components/v2/ui/Loading/Loading';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import { Trash2 } from 'lucide-react';
import Dropzone from 'react-dropzone';
import { makeStyles } from 'tss-react/mui';
import PickImage from '../../../../components/FormInputs/PickMedia';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../translation/TranslationContext';
import SettingContainer from '../../SettingContainer';
import AutoContractGenerationAutocomplete from '../../components/AutoContractGenerationAutocomplete/AutoContractGenerationAutocomplete';
import OrderContractSignatureRequired from '../../enums/orderContractSignatureRequired.enum';
import { getContractSignatureSettingItemLangEnum } from '../../utils/orderContractSignatureSettings.utils';
import { OrderContractSectionGeneralSettingsProps } from '../types/GeneralSettings.types';

const useStyles = makeStyles()((theme) => ({
  dropzoneContainer: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: theme.palette.custom.border.primary,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: 'center',
  },
}));

const OrderContractSectionGeneralSettings = ({
  generalSettings,
  handleChangeAndBlur,
  contract,
  link,
  isLoadingContract,
  handleChangeFile,
  handleDeleteFile,
}: OrderContractSectionGeneralSettingsProps) => {
  const { classes } = useStyles();
  const { translate } = useTranslation();

  const signatureItems = Object.keys(OrderContractSignatureRequired).map((item) => ({
    value: item,
    label: translate(
      getContractSignatureSettingItemLangEnum(item as OrderContractSignatureRequired),
    ),
  }));

  return (
    <SettingContainer
      title={translate(LangEnum.GENERAL_SETTINGS_ORDER_CONTRACT_TITLE)}
      description={LangEnum.GENERAL_SETTINGS_ORDER_CONTRACT_DESCRIPTION}
    >
      <Stack sx={{ gap: 2 }}>
        {contract && link ? (
          <Box>
            <Box
              sx={(theme) => ({
                position: 'relative',
                display: 'inline-block',
                mt: 1,
                ml: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: theme.shape.borderRadius,
                boxShadow: theme.shadows[8],
              })}
            >
              <a href={link} target="_blank" rel="noopener noreferrer">
                <img src="/images/pdf-file.png" alt={contract} width={100} height={120} />
              </a>
              <Box
                sx={{
                  position: 'absolute',
                  top: '12%',
                  left: '85%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <IconButton
                  icon={Trash2}
                  size="large"
                  ariaLabel={translate(LangEnum.GLOBAL_DELETE)}
                  onClick={handleDeleteFile}
                />
              </Box>
            </Box>
            <Typography sx={{ mt: 1 }}>{contract}</Typography>
          </Box>
        ) : (
          <PickImage accept="pdf/*" id="pick-image" onChange={handleChangeFile}>
            <Dropzone accept={['application/pdf']} maxFiles={1} onDropAccepted={handleChangeFile}>
              {({ getRootProps }) => (
                <Box
                  {...getRootProps()}
                  className={classes.dropzoneContainer}
                  sx={{
                    cursor: 'pointer',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                  }}
                >
                  {isLoadingContract && <Loading />}
                  <Typography>{translate(LangEnum.PRODUCT_COLORS_FORM_PICK_IMAGE)}</Typography>
                </Box>
              )}
            </Dropzone>
          </PickImage>
        )}

        <AutoContractGenerationAutocomplete
          value={generalSettings.autoContractGeneration}
          onChange={(value) =>
            handleChangeAndBlur({ value: value ?? null, name: 'autoContractGeneration' })
          }
        />

        <Select
          label={translate(
            LangEnum.ORDER_RULE_ORDER_CONTRACT_SIGNATURE_REQUIRED_SETTING_DESCRIPTION,
          )}
          name="orderContractSignatureRequired"
          value={generalSettings.orderContractSignatureRequired ?? ''}
          renderValue={(selected) =>
            signatureItems.find((item) => item.value === selected)?.label ?? ''
          }
          onChange={(event) =>
            handleChangeAndBlur({
              value: event.target.value as string,
              name: 'orderContractSignatureRequired',
            })
          }
        >
          {signatureItems.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </Stack>
    </SettingContainer>
  );
};

export default OrderContractSectionGeneralSettings;
